const API_BASE = '/_api/public';
const FALLBACK_API_BASE = 'https://api.dev.auxo.dk/public';

async function fetchCms(path) {
  try {
    const res = await fetch(`${API_BASE}${path}`);
    if (res.ok) return await res.json();
  } catch {
    // try fallback
  }
  const fallbackRes = await fetch(`${FALLBACK_API_BASE}${path}`);
  if (!fallbackRes.ok) throw new Error(`HTTP ${fallbackRes.status}`);
  return await fallbackRes.json();
}

/**
 * Extract localized string from a value that may be a string or a language-keyed map { da: "...", en: "..." }
 */
export function getLocalized(val, lang = 'da') {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    return val;
  }
  if (typeof val === 'object' && !Array.isArray(val)) {
    if (val[lang] !== undefined && val[lang] !== null) return val[lang];
    if (val['da'] !== undefined && val['da'] !== null) return val['da'];
    if (val['_'] !== undefined && val['_'] !== null) return val['_'];
    const first = Object.values(val)[0];
    return first !== undefined && first !== null ? first : '';
  }
  return val;
}

/**
 * Safely extracts slug for an entry given a language
 */
export function getSlug(entry, lang = 'da') {
  if (!entry) return '';
  const s = entry.slug;
  if (!s) return entry.id || '';
  if (typeof s === 'string') return s;
  if (typeof s === 'object') {
    return s[lang] || s._ || s.da || Object.values(s)[0] || entry.id || '';
  }
  return String(s);
}

/**
 * Check if an entry matches a target slug in any language or given language
 */
export function entryMatchesSlug(entry, targetSlug, lang = null) {
  if (!entry || !targetSlug) return false;
  if (lang) {
    const s = getSlug(entry, lang);
    if (s === targetSlug) return true;
  }
  // Check any slug variant
  const s = entry.slug;
  if (typeof s === 'string') return s === targetSlug;
  if (typeof s === 'object' && s !== null) {
    if (s.da === targetSlug || s.en === targetSlug || s._ === targetSlug) return true;
    if (Object.values(s).includes(targetSlug)) return true;
  }
  return entry.id === targetSlug;
}

/**
 * Resolve all localized fields of an entry for a specific language
 */
export function resolveEntry(entry, lang = 'da') {
  if (!entry || typeof entry !== 'object') return entry;
  const res = { ...entry };

  for (const [key, val] of Object.entries(entry)) {
    if (
      val &&
      typeof val === 'object' &&
      !Array.isArray(val) &&
      key !== 'terms' &&
      (val.da !== undefined || val.en !== undefined || val._ !== undefined)
    ) {
      res[key] = getLocalized(val, lang);
    }
  }

  // Ensure slug object has robust fallbacks and toString
  const currentSlug = getSlug(entry, lang);
  const rawSlug = typeof entry.slug === 'object' && entry.slug !== null ? entry.slug : {};
  const slugObj = {
    ...rawSlug,
    _: rawSlug._ || currentSlug,
    da: rawSlug.da || currentSlug,
    en: rawSlug.en || currentSlug,
  };
  slugObj.toString = () => currentSlug;
  slugObj.valueOf = () => currentSlug;
  res.slug = slugObj;
  res.currentSlug = currentSlug;

  return res;
}

/**
 * Fetch an entire collection (array shape)
 */
export async function fetchCollection(collectionName) {
  try {
    const data = await fetchCms(`/content/${collectionName}`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn(`Failed to fetch CMS collection "${collectionName}":`, err);
    return [];
  }
}

/**
 * Fetch an entire collection and automatically resolve localized fields for a specific language
 */
export async function fetchLocalizedCollection(collectionName, lang = 'da') {
  const items = await fetchCollection(collectionName);
  return items.map((item) => resolveEntry(item, lang));
}

/**
 * Fetch a collection with pagination/filtering via v2 endpoint
 */
export async function fetchCollectionV2(collectionName, params = {}) {
  try {
    const query = new URLSearchParams(params).toString();
    return await fetchCms(`/v2/content/${collectionName}${query ? `?${query}` : ''}`);
  } catch (err) {
    console.warn(`Failed to fetch CMS collection v2 "${collectionName}":`, err);
    return { items: [], total: 0, limit: 25, offset: 0 };
  }
}

/**
 * Fetch a single entry by its slug (non-localized)
 */
export async function fetchBySlug(slug) {
  try {
    return await fetchCms(`/by-slug/${slug}`);
  } catch (err) {
    console.warn(`Failed to fetch entry by slug "${slug}":`, err);
    return null;
  }
}
